import {
  InventoryListing,
  useUpdateInventoryManualFields,
  useHoldInventory,
  useSetRecommendedPrice,
  useAcknowledgeRecommendedPrice,
  useGetMe,
  getListInventoryQueryKey,
  getGetDashboardSummaryQueryKey,
  getListEventsQueryKey,
} from "@workspace/api-client-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { BankNumbersPopover } from "@/components/bank-numbers";
import { PhoneStatusIcon } from "@/components/phone-status-icon";
import {
  RowActionsCell,
  FveStatusPill,
  PriceCell,
  QrCoverageCell,
  LinkCoverageCell,
  seatSummary,
  SeatPill,
  displayQty,
} from "@/components/inventory-cells";
import { QrCode, Link2 } from "lucide-react";
import { InventoryMobileCard } from "@/components/inventory-card";
import { useFveTransitions } from "@/lib/use-fve-transitions";
import { showHoldOutcomeToast } from "@/lib/hold-toast";
import { useCallback, useMemo, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  useResizableColumns,
  ColumnResizeHandle,
  ResetColumnWidthsButton,
} from "@/hooks/use-resizable-columns";

// Flat, cross-event results for a global listing-ID / account-email lookup.
// Unlike the grouped By Event / By Account tables, every row carries explicit
// Event and Puller columns so operators know where each listing lives.
const COLUMN_ORDER = [
  "actions",
  "fve",
  "event",
  "puller",
  "seats",
  "qty",
  "price",
  "qr",
  "link",
  "account",
  "phone",
  "bank",
] as const;

const COL_WIDTHS: Record<string, string> = {
  actions: "104px",
  fve: "minmax(150px, 0.9fr)",
  event: "minmax(180px, 1.6fr)",
  puller: "minmax(110px, 1fr)",
  seats: "130px",
  qty: "52px",
  price: "120px",
  qr: "40px",
  link: "40px",
  account: "minmax(220px, 1.8fr)",
  phone: "56px",
  bank: "150px",
};

const COL_LABELS: Record<string, string> = {
  actions: "Actions",
  fve: "FVE",
  event: "Event",
  puller: "Puller",
  seats: "Seats",
  qty: "Qty",
  price: "Price",
  qr: "QR",
  link: "Links",
  account: "Account",
  phone: "Phone",
  bank: "Bank",
};

interface GlobalSearchResultsProps {
  data: InventoryListing[];
  defaultHoldDays: number;
}

export function GlobalSearchResults({
  data,
  defaultHoldDays,
}: GlobalSearchResultsProps) {
  const queryClient = useQueryClient();

  // Global results span events and pullers, so invalidate the cross-event
  // inventory list (prefix match) plus the dashboard/events summaries.
  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: getListInventoryQueryKey() });
    queryClient.invalidateQueries({ queryKey: getGetDashboardSummaryQueryKey() });
    queryClient.invalidateQueries({ queryKey: getListEventsQueryKey() });
  };

  const manualFields = useUpdateInventoryManualFields({ mutation: { onSuccess: invalidate } });
  const hold = useHoldInventory({
    mutation: {
      onSuccess: (outcome, variables) => {
        invalidate();
        showHoldOutcomeToast(outcome, variables.data.action);
      },
    },
  });
  const fve = useFveTransitions(invalidate);

  const { data: me } = useGetMe();
  const isAdmin = me?.role === "admin";
  const myPullerTag = me?.pullerTag ?? null;

  const setRecommended = useSetRecommendedPrice({
    mutation: {
      onSuccess: (res) => {
        invalidate();
        if (res.slackAlert.ok) {
          toast.success("Recommended price saved — puller notified via Slack");
        } else {
          toast.warning(
            `Recommended price saved, but the Slack alert failed${res.slackAlert.error ? `: ${res.slackAlert.error}` : ""}`,
          );
        }
      },
      onError: () => toast.error("Failed to save recommended price"),
    },
  });

  const acknowledgeRecommended = useAcknowledgeRecommendedPrice({
    mutation: {
      onSuccess: () => {
        invalidate();
        toast.success("Acknowledged");
      },
      onError: () => toast.error("Failed to acknowledge"),
    },
  });

  const busyId = manualFields.isPending
    ? manualFields.variables?.id
    : hold.isPending
      ? hold.variables?.id
      : (fve.busyId ?? null);

  const isMobile = useIsMobile();

  const resizable = useResizableColumns("tex-global-col-widths", COL_WIDTHS);
  // Global search spans every event, so it can be genuinely mixed. Show a
  // coverage column only when at least one row belongs to that delivery type
  // (QR for international, Links for domestic), driven by the event's configured
  // marketType rather than whether assets are uploaded yet.
  const showQrColumn = useMemo(
    () => data.some((r) => r.marketType === "international"),
    [data],
  );
  const showLinkColumn = useMemo(
    () => data.some((r) => r.marketType === "domestic"),
    [data],
  );
  const columnOrder = useMemo(
    () =>
      COLUMN_ORDER.filter((id) => {
        if (id === "qr") return showQrColumn;
        if (id === "link") return showLinkColumn;
        return true;
      }),
    [showQrColumn, showLinkColumn],
  );
  const { template: gridTemplate, minWidth: gridMinWidth } = resizable.getLayout([
    ...columnOrder,
  ]);

  // Header mirrors the body's horizontal scroll so columns stay aligned when
  // resized widths overflow the panel.
  const headerScrollRef = useRef<HTMLDivElement>(null);
  const syncHeaderScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (headerScrollRef.current) {
      headerScrollRef.current.scrollLeft = e.currentTarget.scrollLeft;
    }
  }, []);

  if (data.length === 0) {
    return (
      <div className="h-full flex items-center justify-center text-center text-sm text-muted-foreground p-10">
        No listings match this search across any event.
      </div>
    );
  }

  if (isMobile) {
    return (
      <div className="h-full overflow-auto custom-scrollbar p-2 space-y-2">
        {data.map((r) => (
          <InventoryMobileCard
            key={r.id}
            row={r}
            fve={fve}
            defaultHoldDays={defaultHoldDays}
            isBusy={busyId === r.id}
            onHold={(days) => hold.mutate({ id: r.id, data: { action: "hold", holdDays: days } })}
            onUnhold={() => hold.mutate({ id: r.id, data: { action: "unhold" } })}
            isAdmin={isAdmin}
            canAcknowledge={myPullerTag != null && myPullerTag === r.pullerTag}
            onSavePrice={(price) =>
              setRecommended.mutateAsync({
                id: r.id,
                data: { recommendedListPrice: price },
              })
            }
            onAcknowledge={() => acknowledgeRecommended.mutateAsync({ id: r.id })}
            extraFields={[
              {
                label: "Event",
                node: r.eventName || (
                  <span className="text-muted-foreground">—</span>
                ),
                full: true,
              },
              {
                label: "Puller",
                node: r.pullerName || (
                  <span className="text-muted-foreground">Unassigned</span>
                ),
              },
              {
                label: "Account",
                node: (
                  <span className="break-all text-xs">
                    {r.vendorAccountEmail || "-"}
                  </span>
                ),
              },
            ]}
            showPhone
            showBank
            showQr
            showLink
          />
        ))}
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {resizable.isCustomized && (
        <div className="flex items-center justify-end px-2 py-1 border-b border-border/40 shrink-0">
          <ResetColumnWidthsButton show onReset={resizable.reset} />
        </div>
      )}

      {/* Column header (outside the scroll region so it never scrolls away).
          Horizontally it mirrors the body's scroll so columns stay aligned. */}
      <div ref={headerScrollRef} className="overflow-hidden shrink-0">
        <div
          className="grid bg-panel border-b border-border"
          style={{ gridTemplateColumns: gridTemplate, minWidth: gridMinWidth, height: 36 }}
        >
          {columnOrder.map((id) => (
            <div
              key={id}
              className="relative flex items-center px-3 text-[10px] uppercase tracking-wider text-muted-foreground font-mono font-medium whitespace-nowrap select-none overflow-hidden"
            >
              {id === "qr" ? (
                <span title="QR / PDF assets" aria-label="QR / PDF assets">
                  <QrCode className="h-3.5 w-3.5" />
                </span>
              ) : id === "link" ? (
                <span title="Transfer links" aria-label="Transfer links">
                  <Link2 className="h-3.5 w-3.5" />
                </span>
              ) : (
                COL_LABELS[id]
              )}
              <ColumnResizeHandle columnId={id} onResize={resizable.startResize} />
            </div>
          ))}
        </div>
      </div>

      <div
        className="flex-1 overflow-auto custom-scrollbar"
        onScroll={syncHeaderScroll}
      >
        <div style={{ minWidth: gridMinWidth }}>
        {data.map((r) => (
          <div
            key={r.id}
            className="grid items-center border-b border-border/40 hover:bg-muted/30 transition-colors"
            style={{ gridTemplateColumns: gridTemplate, minHeight: 37 }}
          >
            <div className="px-3">
              <RowActionsCell
                row={r}
                defaultHoldDays={defaultHoldDays}
                isBusy={busyId === r.id}
                onHold={(days) => hold.mutate({ id: r.id, data: { action: "hold", holdDays: days } })}
                onUnhold={() => hold.mutate({ id: r.id, data: { action: "unhold" } })}
                fve={fve}
              />
            </div>
            <div className="px-3 overflow-hidden flex items-center">
              <FveStatusPill row={r} fve={fve} />
            </div>
            <div className="px-3 text-xs truncate" title={r.eventName ?? undefined}>
              {r.eventName || <span className="text-muted-foreground">—</span>}
            </div>
            <div className="px-3 text-xs truncate" title={r.pullerName ?? undefined}>
              {r.pullerName || <span className="text-muted-foreground">Unassigned</span>}
            </div>
            <div className="px-3 flex items-center">
              <SeatPill seat={seatSummary(r)} />
            </div>
            <div className="px-3 font-mono text-xs">{displayQty(r)}</div>
            <div className="px-3 overflow-hidden flex items-center">
              <PriceCell
                row={r}
                isAdmin={isAdmin}
                canAcknowledge={
                  myPullerTag != null && myPullerTag === r.pullerTag
                }
                onSave={(price) =>
                  setRecommended.mutateAsync({
                    id: r.id,
                    data: { recommendedListPrice: price },
                  })
                }
                onAcknowledge={() =>
                  acknowledgeRecommended.mutateAsync({ id: r.id })
                }
              />
            </div>
            {showQrColumn && (
              <div className="px-3 overflow-hidden flex items-center justify-center">
                <QrCoverageCell row={r} />
              </div>
            )}
            {showLinkColumn && (
              <div className="px-3 overflow-hidden flex items-center justify-center">
                <LinkCoverageCell row={r} />
              </div>
            )}
            <div className="px-3 text-xs truncate" title={r.vendorAccountEmail ?? undefined}>
              {r.vendorAccountEmail || "-"}
            </div>
            <div className="px-3">
              <PhoneStatusIcon
                status={r.phoneStatus as "good" | "bad" | "unknown" | null}
                lastSmsTime={r.lastSmsTime}
                phoneType={r.phoneType}
                phone={r.phoneOnFile}
              />
            </div>
            <div className="px-3">
              {!r.bankMappingStatus ? (
                <Badge
                  variant="outline"
                  className="bg-amber-500/10 text-amber-500 border-amber-500/30 font-mono text-[9px] uppercase"
                >
                  No Bank
                </Badge>
              ) : (
                <BankNumbersPopover
                  details={{
                    label: r.bankCardName || r.bankName || r.bankSource || "Bank",
                    variant: r.bankVariant,
                    routingNumber: r.bankRouting,
                    accountNumber: r.bankAccount,
                    routingLast4: r.bankRoutingLast4,
                    accountLast4: r.bankAccountLast4,
                    variantId: r.bankVariantId,
                    mappingStatus: r.bankMappingStatus,
                    accountEmail: r.vendorAccountEmail,
                  }}
                />
              )}
            </div>
          </div>
        ))}
        </div>
      </div>
    </div>
  );
}
